import {
  type FilterStatus,
  swapsExportToCSV,
  SwapsTabView,
  type SwapData,
  useInterval,
} from '@valiant/ui-shared'
import { environment } from '@/environment'
import {
  useGetSwapsQuery,
  useApproveSwapMutation,
  useRejectSwapMutation,
  useFlagSwapMutation,
  type TransactionStatus,
} from '@/generated/graphql'
import { useState, useMemo, useRef } from 'react'
import { useDebounce } from '@valiant/ui-shared'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/AuthProvider'

interface SwapsTabProps {
  userId?: string
  companyId?: string
  groupId?: string
  searchTerm?: string
}

export function SwapsTab({ userId, companyId, groupId, searchTerm }: SwapsTabProps) {
  const navigate = useNavigate()
  const { auth } = useAuth()
  const isComplianceUser = auth?.role === 'COMPLIANCE'
  const [selectedStatus, setSelectedStatus] = useState<FilterStatus | null>(null)
  const [selectedFlag, setSelectedFlag] = useState<string | null>(null)
  // Use the existing debounce hook
  const debouncedSearch = useDebounce(searchTerm || '')

  const filter = useMemo(() => {
    interface FilterObject {
      userId?: string
      companyId?: string
      groupId?: string
      search?: string
      status?: TransactionStatus
      isFlagged?: boolean
    }

    const filterObj: FilterObject = {}
    if (userId) filterObj.userId = userId
    if (companyId) filterObj.companyId = companyId
    if (groupId) filterObj.groupId = groupId
    if (debouncedSearch.trim()) filterObj.search = debouncedSearch.trim()

    if (selectedStatus) filterObj.status = selectedStatus as unknown as TransactionStatus
    if (selectedFlag) filterObj.isFlagged = selectedFlag === 'FLAGGED'
    return Object.keys(filterObj).length > 0 ? filterObj : undefined
  }, [userId, companyId, groupId, debouncedSearch, selectedStatus, selectedFlag])

  const [{ data: swapsData, fetching: swapsLoading, error: _swapsError }, refetchSwaps] =
    useGetSwapsQuery({
      variables: { filter },
    })

  // Track whether the initial fetch has completed to avoid flickering on background refetches
  const hasLoadedRef = useRef(false)
  if (!swapsLoading) hasLoadedRef.current = true

  // Auto-refetch every 10 seconds to catch status changes, without showing loading state
  useInterval(() => {
    refetchSwaps({ requestPolicy: 'network-only' })
  }, 10000)

  // State for manual refresh loading
  const [isManualRefreshing, setIsManualRefreshing] = useState(false)

  const [, approveSwap] = useApproveSwapMutation()
  const [, rejectSwap] = useRejectSwapMutation()
  const [, flagSwap] = useFlagSwapMutation()

  const swaps: SwapData[] = (swapsData?.getSwaps.nodes as SwapData[]) || []

  const handleApproveSwap = async (swapId: string) => {
    try {
      await approveSwap({ input: { swapId } })
      toast.success('Swap approved successfully')
    } catch (error) {
      toast.error('Failed to approve swap')
      console.error('Error approving swap:', error)
      throw error
    }
  }

  const handleRejectSwap = async (swapId: string, reason: string) => {
    try {
      const result = await rejectSwap({ input: { swapId, reason } })
      if (result.error) {
        throw new Error(result.error.message)
      }
      toast.success('Swap rejected successfully')
    } catch (error) {
      toast.error('Failed to reject swap')
      console.error('Error rejecting swap:', error)
      throw error
    }
  }

  const handleFlagSwap = async (swapId: string, flagged: boolean) => {
    try {
      const result = await flagSwap({ input: { swapId, flagged } })
      if (result.error) {
        throw new Error(result.error.message)
      }
      toast.success(flagged ? 'Swap flagged successfully' : 'Swap unflagged successfully')
    } catch (error) {
      toast.error('Failed to toggle swap flag')
      console.error('Error toggling swap flag:', error)
      throw error
    }
  }

  const handleViewSwapDetails = (swapId: string) => {
    navigate(`/transactions/swap/${swapId}`)
  }

  const handleStatusChange = (status: FilterStatus | null) => {
    setSelectedStatus(status)
  }

  const handleFlagChange = (flag: string | null) => {
    setSelectedFlag(flag)
  }

  const exportToCSV = () => {
    swapsExportToCSV(swaps, environment.MSB)
  }

  const handleManualRefresh = () => {
    setIsManualRefreshing(true)
    try {
      refetchSwaps({ requestPolicy: 'network-only' })
      toast.success('Swaps refreshed successfully')
    } catch (error) {
      toast.error('Failed to refresh swaps')
      console.error('Error refreshing swaps:', error)
    } finally {
      setIsManualRefreshing(false)
    }
  }

  return (
    <SwapsTabView
      swaps={swaps}
      loading={swapsLoading && !hasLoadedRef.current}
      title={userId ? 'User Swaps' : 'All Swaps'}
      showExportButton={true}
      showRefreshButton={true}
      showApproveButton={true}
      showRejectButton={true}
      showFlagButton={true}
      isComplianceUser={isComplianceUser}
      onApproveSwap={handleApproveSwap}
      onRejectSwap={handleRejectSwap}
      onFlagSwap={handleFlagSwap}
      onViewSwapDetails={handleViewSwapDetails}
      onRefresh={handleManualRefresh}
      isRefreshing={isManualRefreshing}
      selectedStatus={selectedStatus}
      onStatusChange={handleStatusChange}
      onFlagChange={handleFlagChange}
      selectedFlag={selectedFlag}
      onExportToCSV={exportToCSV}
    />
  )
}
